// A 2D map's reading of `complex_property` Geospatial rows: one GeoObject per
// value, every variant, positions in Leaflet's [lat, lon] order. The variant
// walk is `partsOf` (geoValue.ts); this only adds the label anchor (centroid)
// and the part each position list belongs to. The events, the hover lighting
// and the Leaflet path styles of the shapes follow.
import { partsOf, type GeoValue } from './geoValue';
import { MARKER_SELECTION } from './markerIcons';
import type { LatLngLike } from './relationshipEdges';

/** One Geospatial value of a twin, as a map draws it (`extractGeoObjects`). */
export interface GeoObject {
  propertyId: bigint;
  name: string;
  geoType: string; // 'Point' | 'LineString' | 'Polygon' | 'MultiPoint' | 'MultiLineString' | 'MultiPolygon'
  positions: [number, number][][]; // array of rings/lines (each ring is an array of [lat, lon])
  /** The part (of a Multi* value) each `positions` entry belongs to; 0 for a single shape. */
  parts: number[];
  centroid: [number, number] | null;
}

interface ComplexPropertyRow {
  propertyId: bigint;
  name: string;
  value?: { tag: string; value?: unknown } | null;
}

const GEO_KINDS = new Set([
  'Point',
  'MultiPoint',
  'LineString',
  'MultiLineString',
  'Polygon',
  'MultiPolygon',
]);

export function extractGeoObjects(complexProperties: Iterable<ComplexPropertyRow>): GeoObject[] {
  const result: GeoObject[] = [];
  for (const cp of complexProperties) {
    if (cp.value?.tag !== 'Geospatial' || !cp.value.value) continue;
    const geo = cp.value.value as GeoValue;
    if (!GEO_KINDS.has(geo.tag)) continue;
    const positions = partsOf(geo).map((part) => part.map((v): [number, number] => [v.lat, v.lon]));
    result.push({
      propertyId: cp.propertyId,
      name: cp.name,
      geoType: geo.tag,
      positions,
      parts: partIndexes(geo, positions.length),
      centroid: centroidOf(geo.tag, positions),
    });
  }
  return result;
}

/** The part of each of `partsOf`'s lists: a MultiPolygon part is all its rings, another Multi* part one list, a single shape is part 0. */
function partIndexes(geo: GeoValue, count: number): number[] {
  if (geo.tag === 'MultiPolygon') return geo.value.polygons.flatMap((p, i) => p.rings.map(() => i));
  return Array.from({ length: count }, (_, i) => (geo.tag.startsWith('Multi') ? i : 0));
}

/** The object with one part left out (the part a scene edits draws it itself). */
export function withoutGeoPart(geo: GeoObject, part: number): GeoObject {
  return {
    ...geo,
    positions: geo.positions.filter((_, i) => geo.parts[i] !== part),
    parts: geo.parts.filter((p) => p !== part),
  };
}

/** A line's middle vertex, a polygon's outer-ring mean, otherwise the first point. */
function centroidOf(tag: string, positions: [number, number][][]): [number, number] | null {
  const first = positions[0];
  if (!first || first.length === 0) return null;
  if (tag === 'LineString') return first[Math.floor(first.length / 2)];
  if (tag === 'Polygon') {
    const lat = first.reduce((s, p) => s + p[0], 0) / first.length;
    const lon = first.reduce((s, p) => s + p[1], 0) / first.length;
    return [lat, lon];
  }
  return first[0];
}

/** Where a twin's shapes anchor it on a map: the first shape's centroid, else its first
 *  position, as `{ lat, lng }`; null when the twin has no shape or the shape no position.
 *  The end of a relationship line to a twin that has no marker. */
export function geoAnchorOf(
  geoObjects: readonly GeoObject[] | null | undefined,
): LatLngLike | null {
  const geo = geoObjects?.[0];
  const at = geo?.centroid ?? geo?.positions[0]?.[0];
  return at ? { lat: at[0], lng: at[1] } : null;
}

/** Uniform Catmull-Rom through `ps`, the 3D viewer's line look (Bevy `geo_properties::smooth`,
 *  same subdivision), so a shape reads the same on the map and in 3D. `closed` wraps the
 *  neighbours so a loop has no corner at its seam (a closing duplicate of the first vertex is
 *  dropped first, and the smoothed loop comes back closed, so a line draws all the way round).
 *  Passes through every input vertex; fewer than 3 come back unchanged. Draw-only: editing and
 *  hit-testing keep the raw vertices. */
export function smoothPath(ps: readonly [number, number][], closed: boolean): [number, number][] {
  const last = ps[ps.length - 1];
  const verts =
    closed && ps.length >= 2 && ps[0][0] === last[0] && ps[0][1] === last[1] ? ps.slice(0, -1) : ps;
  const n = verts.length;
  if (n < 3) return [...ps];
  const sub = Math.min(8, Math.max(1, Math.floor(2048 / n)));
  const at = (i: number) => verts[closed ? (i + n) % n : Math.min(Math.max(i, 0), n - 1)];
  const cr = (p0: number, p1: number, p2: number, p3: number, t: number) =>
    0.5 *
    (2 * p1 +
      (-p0 + p2) * t +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t +
      (-p0 + 3 * p1 - 3 * p2 + p3) * t * t * t);
  const out: [number, number][] = [];
  for (let i = 0; i < (closed ? n : n - 1); i++) {
    const [p0, p1, p2, p3] = [at(i - 1), at(i), at(i + 1), at(i + 2)];
    for (let s = 0; s < sub; s++) {
      const t = s / sub;
      out.push([cr(p0[0], p1[0], p2[0], p3[0], t), cr(p0[1], p1[1], p2[1], p3[1], t)]);
    }
  }
  out.push(closed ? out[0] : verts[n - 1]);
  return out;
}

/** A shape's Leaflet event handlers: a click (`onClick`), a right-click at
 *  the pointer in viewport px (`onContextMenu`, the browser's own menu
 *  suppressed) and the pointer entering and leaving it (`onHover`), each
 *  with the property and the part the shape draws. A marker or relationship
 *  line drawn over the shape takes the event first, as it does for a click.
 *  Undefined when none is given. */
export function geoShapeEvents(
  propertyName: string,
  part: number,
  onClick?: (propertyName: string, part: number) => void,
  onContextMenu?: (propertyName: string, part: number, x: number, y: number) => void,
  onHover?: (propertyName: string, part: number, on: boolean) => void,
):
  | {
      click?: () => void;
      contextmenu?: (e: {
        originalEvent: { preventDefault(): void; clientX: number; clientY: number };
      }) => void;
      mouseover?: () => void;
      mouseout?: () => void;
    }
  | undefined {
  if (!onClick && !onContextMenu && !onHover) return undefined;
  return {
    ...(onClick && { click: () => onClick(propertyName, part) }),
    ...(onContextMenu && {
      contextmenu: ({ originalEvent: e }) => {
        e.preventDefault();
        onContextMenu(propertyName, part, e.clientX, e.clientY);
      },
    }),
    ...(onHover && {
      mouseover: () => onHover(propertyName, part, true),
      mouseout: () => onHover(propertyName, part, false),
    }),
  };
}

/** A geo shape or a whole property to light up in the hover colour: `part`
 *  null = every part of the property. */
export interface GeoHoverTarget {
  propertyName: string;
  part: number | null;
}

/** Whether the shape drawing `part` of `propertyName` is lit by one of
 *  `targets` (the cursor's shape, a menu row's property or part). */
export function geoShapeLit(
  targets: readonly (GeoHoverTarget | null | undefined)[],
  propertyName: string,
  part: number,
): boolean {
  return targets.some(
    (t) => t != null && t.propertyName === propertyName && (t.part == null || t.part === part),
  );
}

/** The hover colour of a geospatial shape, the same one the 3D viewer uses. */
export const GEO_HOVER_COLOR = '#9be7ff';

/** The colours a geospatial shape takes besides its own: the stroke and fill of a
 *  selected twin's shapes, and of a lit one. */
export interface GeoShapeColors {
  selected: string;
  hover: string;
}

/** The default `GeoShapeColors`: the markers' selection colour, and `GEO_HOVER_COLOR`. */
export const GEO_SHAPE_COLORS: GeoShapeColors = {
  selected: MARKER_SELECTION.selected,
  hover: GEO_HOVER_COLOR,
};

/** The pathOptions of one layer's shapes, per kind, lit or not. Built once
 *  per selection and colour: react-leaflet restyles a path only when its
 *  pathOptions object changes, so a hover restyles just the shapes whose
 *  state changed. Lit = the hover colour, stroke and fill. */
export function geoPathStyles(
  selected: boolean,
  color: string,
  lit: boolean,
  colors: GeoShapeColors = GEO_SHAPE_COLORS,
) {
  // Unselected geometry draws at a weight that stays readable over satellite
  // tiles (thin lines were not); selection is the stroke colour, as for the
  // markers.
  const stroke = lit ? colors.hover : selected ? colors.selected : color;
  const fill = stroke;
  const weight = selected ? 4 : 2.5;
  const opacity = 0.9;
  return {
    polygon: {
      color: stroke,
      fillColor: fill,
      fillOpacity: selected ? 0.35 : 0.15,
      weight,
      opacity,
    },
    // The visible line takes no events; its hit buffer (`hit`) does.
    line: { color: stroke, weight, opacity, interactive: false },
    hit: { color, weight: 18, opacity: 0 },
    point: {
      color: stroke,
      fillColor: fill,
      fillOpacity: 0.7,
      weight: selected ? 2.5 : 1.5,
      opacity,
    },
  };
}
